import type { AnalyticsService } from '@thinkalpha/ui-ioc/core/types/AnalyticsService';
import type { DatadogService } from '@thinkalpha/ui-ioc/core/types/DatadogService';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { optional } from 'inversify';
import { inject, injectable } from 'src/features/ioc';
import { PERSISTED_CURRENT_USER } from 'src/features/local-storage';
import { localforage } from 'src/lib/serialization/localForage';
import type { RootState } from 'src/store';
import { getUser } from 'src/store/selectors';
import type { IsomorphicBindings } from 'src/types/bindings';

/**
 * Syncs various data from the store to external services
 */
@injectable()
export class SyncToExternalsServiceImpl {
    #logger: Logger;

    constructor(
        @inject('Store') private readonly store: IsomorphicBindings['Store'],
        @inject('Logger') baseLogger: Logger,
        @inject('DatadogService') @optional() private readonly datadogService?: DatadogService,
        @inject('AnalyticsService') @optional() private readonly analyticsService?: AnalyticsService,
    ) {
        this.#logger = baseLogger.getSubLogger({ name: 'feat:electron-renderer:syncToExternalsService' });

        this.store
            .subscribeSelector(getUser, { leading: true })
            .handle((userDetails) => this.syncUserDetails(userDetails));
    }

    private async syncUserDetails(userDetails: RootState['auth']['user']) {
        if (TRACE) {
            this.#logger.trace('Setting user details', { userDetails });
        }

        try {
            void localforage.setItem(PERSISTED_CURRENT_USER, userDetails);
            this.datadogService?.setUserDetails(userDetails);

            // Sync user to analytics service
            if (userDetails) {
                this.analyticsService?.identify(userDetails);
            } else {
                this.analyticsService?.clearUser();
            }
        } catch (e) {
            this.#logger.error('Failed to sync user details', { err: e });
        }
    }
}
